import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Check, ChevronLeft, ChevronRight, ChevronDown, Download, Search as SearchIcon, ArrowRight } from "lucide-react";
import "../../Styles/FinanceScreen.css";

import { apiGet, apiPost } from "../../Api/Client.js";
import { money } from "../../Shared/ScreenUtils.jsx";
import { loadList } from "./loadList.js";
import { accountLabel, approveAfterConfirm, reapprovalNote } from "./batchApproval.js";
import { visibleSelectedIds } from "./visibleSelection.js";
import { createLatestGate } from "../../Shared/latestRequest.js";

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export function FinanceScreen({ data, reload }) {
  const [tab, setTab] = useState("stage1");
  const [monthIndex, setMonthIndex] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());

  const [stage1Employees, setStage1Employees] = useState([]);
  const [stage1Loading, setStage1Loading] = useState(false);
  const [stage1Error, setStage1Error] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [advancing, setAdvancing] = useState(false);

  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batchesError, setBatchesError] = useState("");
  const [confirmFor, setConfirmFor] = useState(null);

  const month = monthIndex + 1;

  // Stepping through months quickly: only the newest answer may land, or one month's people would
  // sit under another month's name and be posted with it.
  const stage1Gate = useMemo(createLatestGate, []);
  const batchesGate = useMemo(createLatestGate, []);

  const fetchStage1 = useCallback(async () => {
    const ticket = stage1Gate.start();
    setStage1Loading(true);
    const out = await loadList(
      () => apiGet(`/FinanceAndPayroll/stage1-employees/?month=${month}&year=${year}`),
      (res) => res?.employees,
      "The pending employees could not be loaded.",
    );
    if (!stage1Gate.isCurrent(ticket)) return;
    setStage1Employees(out.items);
    setStage1Error(out.error);
    setStage1Loading(false);
  }, [month, year, stage1Gate]);

  const fetchBatches = useCallback(async () => {
    const ticket = batchesGate.start();
    setBatchesLoading(true);
    const out = await loadList(
      () => apiGet(`/FinanceAndPayroll/batches/?month=${month}&year=${year}`),
      (res) => res?.batches,
      "The batches could not be loaded.",
    );
    if (!batchesGate.isCurrent(ticket)) return;
    setBatches(out.items);
    setBatchesError(out.error);
    setBatchesLoading(false);
  }, [month, year, batchesGate]);

  // Both lists load together, so the counts on all three tabs are right from the start, not only after a tab is opened.
  useEffect(() => {
    fetchStage1();
    fetchBatches();
  }, [tab, month, year, fetchStage1, fetchBatches]);

  const changeMonth = (delta) => {
    let m = monthIndex + delta;
    let y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setMonthIndex(m);
    setYear(y);
    setSelected(new Set());
  };

  const filtered = useMemo(() => {
    if (!searchQuery) return stage1Employees;
    const q = searchQuery.toLowerCase();
    return stage1Employees.filter(e => (e.display_name || "").toLowerCase().includes(q) || (e.department || "").toLowerCase().includes(q));
  }, [stage1Employees, searchQuery]);

  const chosenIds = visibleSelectedIds(filtered, selected);
  const totalNet = filtered.reduce((s, e) => s + (e.net_pay || 0), 0);
  const allSelected = filtered.length > 0 && filtered.every(e => selected.has(e.id));
  const toggleAll = () => { if (allSelected) setSelected(new Set()); else setSelected(new Set(filtered.map(e => e.id))); };
  const toggleOne = (id) => { const n = new Set(selected); if (n.has(id)) n.delete(id); else n.add(id); setSelected(n); };

  const advanceSelected = async () => {
    if (!chosenIds.length || advancing) return;
    setAdvancing(true);
    try {
      await apiPost("/FinanceAndPayroll/advance-stage/", { employee_ids: chosenIds, month, year, notes: `Batch — ${MONTH_NAMES[monthIndex]} ${year}` });
      setSelected(new Set());
      fetchStage1();
    } catch (err) { alert(err?.message || "Failed To Create Batch."); }
    finally { setAdvancing(false); }
  };

  // Someone who made a batch and is the only person who can approve it is asked to confirm with their password.
  const approveBatch = async (batchId, confirmPassword) => {
    try {
      await apiPost("/FinanceAndPayroll/approve-batch/", confirmPassword ? { batch_id: batchId, confirm_password: confirmPassword } : { batch_id: batchId });
      setConfirmFor(null);
      fetchBatches();
    } catch (err) {
      if (err?.payload?.code === "confirmation_required") {
        setConfirmFor({ batchId, message: err.payload.message, password: "" });
        return;
      }
      alert(err?.message || "Failed To Approve Batch.");
    }
  };

  const confirmApprove = (batchId) => approveAfterConfirm(batches.find((b) => b.id === batchId), (q) => window.confirm(q), approveBatch);

  const revertBatch = async (batchId) => {
    if (!window.confirm("Revert This BAtch? Employees Will Move Back To Stage 1.")) return;
    try { await apiPost("/FinanceAndPayroll/revert-batch/", { batch_id: batchId }); fetchStage1(); fetchBatches(); }
    catch (err) { alert(err?.message || "Failed To Revert Batch."); }
  };

  const stage2Batches = batches.filter(b => b.stage === "stage_2");
  const stage3Batches = batches.filter(b => b.stage === "stage_3");

  return (
    <section style={{ padding: "24px 32px", fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Finance</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <a href={`/api/FinanceAndPayroll/download-payroll-excel/?month=${month}&year=${year}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", background: "var(--blue-700)", color: "var(--surface)", borderRadius: 8, fontSize: 13, fontWeight: 500, textDecoration: "none" }}>
            <Download size={14} /> Download Payroll Excel
          </a>
          <button onClick={() => changeMonth(-1)} style={{ padding: "6px 10px", border: "1px Solid var(--c-200)", borderRadius: 6, background: "var(--surface)", cursor: "pointer" }}><ChevronLeft size={16} /></button>
          <strong style={{ minWidth: 130, textAlign: "center", fontSize: 14 }}>{MONTH_NAMES[monthIndex]} {year}</strong>
          <button onClick={() => changeMonth(1)} style={{ padding: "6px 10px", border: "1px Solid var(--c-200)", borderRadius: 6, background: "var(--surface)", cursor: "pointer" }}><ChevronRight size={16} /></button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: "2px solid var(--c-200)", marginBottom: 20 }}>
        {[["stage1", `Stage 1: Pending`, stage1Employees.length, "var(--blue-100)", "var(--indigo-500)"],
          ["stage2", `Stage 2: Batched`, stage2Batches.length, "var(--amber-50)", "var(--amber-600)"],
          ["stage3", `Stage 3: Approved`, stage3Batches.length, "var(--green-100)", "var(--green-700)"]
        ].map(([key, label, count, bg, color]) => (
          <button key={key} onClick={() => setTab(key)} style={{ padding: "10px 24px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: tab === key ? "var(--blue-700)" : "var(--c-500)", borderBottom: tab === key ? "2px Solid var(--blue-700)" : "2px Solid Transparent", marginBottom: -2 }}>
            {label} <span style={{ marginLeft: 6, fontSize: 11, background: bg, color, padding: "2px 7px", borderRadius: 10 }}>{count}</span>
          </button>
        ))}
      </div>

      {/* Search + Stats (Stage 1 Only) */}
      {tab === "stage1" && !stage1Error && (
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 14, flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: "1 1 220px", maxWidth: 320 }}>
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search Name Or Department" style={{ width: "100%", padding: "8px 12px 8px 34px", border: "1px Solid var(--c-200)", borderRadius: 8, fontSize: 13 }} />
            <SearchIcon size={14} style={{ position: "absolute", left: 11, top: 10, color: "var(--c-400)" }} />
          </div>
          <span style={{ fontSize: 13, color: "var(--c-500)" }}>{filtered.length} Employees · ₹{Math.round(totalNet).toLocaleString()} Total Net</span>
        </div>
      )}

      {/* Stage 1 Table */}
      {tab === "stage1" && (
        <>
          {stage1Loading ? <div style={{ padding: 40, textAlign: "center", color: "var(--c-400)" }}>Loading...</div> : stage1Error ? <LoadFailed message={stage1Error} onRetry={fetchStage1} /> : (
            <div style={{ overflowX: "auto", border: "1px solid var(--c-200)", borderRadius: 10 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "var(--surface-soft)" }}>
                    <th style={TH}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                    <th style={{ ...TH, textAlign: "left" }}>Name</th>
                    <th style={{ ...TH, textAlign: "left" }}>Position</th>
                    <th style={{ ...TH, textAlign: "left" }}>Department</th>
                    <th style={{ ...TH, textAlign: "left" }}>Team Leader</th>
                    <th style={TH}>Status</th>
                    <th style={{ ...TH, textAlign: "right" }}>Base Pay</th>
                    <th style={{ ...TH, textAlign: "right" }}>Bounty</th>
                    <th style={{ ...TH, textAlign: "center" }}>Leaves</th>
                    <th style={{ ...TH, textAlign: "center" }}>EOD</th>
                    <th style={{ ...TH, textAlign: "right" }}>Net Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(emp => (
                    <tr key={emp.id} style={{ borderBottom: "1px solid var(--c-100)", background: selected.has(emp.id) ? "var(--blue-50)" : "var(--surface)" }}>
                      <td style={TD}><input type="checkbox" checked={selected.has(emp.id)} onChange={() => toggleOne(emp.id)} /></td>
                      <td style={{ ...TD, fontWeight: 500 }}>{emp.display_name}</td>
                      <td style={TD}><PositionBadge type={emp.position_type} /></td>
                      <td style={TD}>{emp.department || "—"}</td>
                      <td style={TD}>{emp.team_leader || "—"}</td>
                      <td style={{ ...TD, textAlign: "center" }}><span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: "var(--green-100)", color: "var(--green-700)" }}>{emp.status}</span></td>
                      <td style={{ ...TD, textAlign: "right" }}>₹{money(emp.base_pay)}</td>
                      <td style={{ ...TD, textAlign: "right" }}>{emp.bounty || 0}</td>
                      <td style={{ ...TD, textAlign: "center" }}>{emp.leaves}</td>
                      <td style={{ ...TD, textAlign: "center" }}>{emp.eod_count}</td>
                      <td style={{ ...TD, textAlign: "right", fontWeight: 700, color: emp.net_pay > 0 ? "var(--green-600)" : "var(--c-500)" }}>₹{money(emp.net_pay)}</td>
                    </tr>
                  ))}
                  {!filtered.length && <tr><td colSpan={11} style={{ textAlign: "center", padding: 40, color: "var(--c-400)" }}>No Pending Employees</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {chosenIds.length > 0 && (
            <div style={{ position: "sticky", bottom: 0, padding: "14px 0", background: "var(--surface-soft)", borderTop: "1px solid var(--c-200)", display: "flex", alignItems: "center", gap: 16, marginTop: 16 }}>
              <strong style={{ fontSize: 14, color: "var(--c-800)" }}>{chosenIds.length} Selected</strong>
              <button onClick={advanceSelected} disabled={advancing} style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, padding: "9px 20px", background: "var(--blue-700)", color: "var(--surface)", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                {advancing ? "Creating Batch..." : <><span>Proceed To Next Stage</span> <ArrowRight size={15} /></>}
              </button>
            </div>
          )}
        </>
      )}

      {/* Stage 2 */}
      {tab === "stage2" && (
        <div>
          {batchesLoading ? <div style={{ padding: 40, textAlign: "center", color: "var(--c-400)" }}>Loading...</div> :
            batchesError ? <LoadFailed message={batchesError} onRetry={fetchBatches} /> :
            stage2Batches.length === 0 ? <div style={{ padding: 40, textAlign: "center", color: "var(--c-400)" }}>No Batches In Stage 2</div> :
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{stage2Batches.map(b => <BatchCard key={b.id} batch={b} onApprove={confirmApprove} onRevert={revertBatch} showApprove />)}</div>}
          {confirmFor && (
            <form onSubmit={e => { e.preventDefault(); approveBatch(confirmFor.batchId, confirmFor.password); }} style={{ marginTop: 14, padding: "14px 20px", border: "1px solid var(--c-200)", borderRadius: 10, background: "var(--surface)", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, flex: "1 1 260px" }}>{confirmFor.message}</span>
              <input type="password" autoComplete="current-password" autoFocus value={confirmFor.password} onChange={e => setConfirmFor({ ...confirmFor, password: e.target.value })} placeholder="Your password" style={{ padding: "6px 10px", border: "1px solid var(--c-200)", borderRadius: 6, fontSize: 13 }} />
              <button type="submit" disabled={!confirmFor.password} style={{ padding: "6px 14px", background: "var(--green-600)", color: "var(--surface)", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Confirm And Approve</button>
              <button type="button" onClick={() => setConfirmFor(null)} style={{ padding: "6px 14px", border: "1px solid var(--c-200)", background: "var(--surface)", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>Cancel</button>
            </form>
          )}
        </div>
      )}

      {/* Stage 3 */}
      {tab === "stage3" && (
        <div>
          {batchesLoading ? <div style={{ padding: 40, textAlign: "center", color: "var(--c-400)" }}>Loading...</div> :
            batchesError ? <LoadFailed message={batchesError} onRetry={fetchBatches} /> :
            stage3Batches.length === 0 ? <div style={{ padding: 40, textAlign: "center", color: "var(--c-400)" }}>No Approved Batches</div> :
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{stage3Batches.map(b => <BatchCard key={b.id} batch={b} showApprove={false} />)}</div>}
        </div>
      )}
    </section>
  );
}

function LoadFailed({ message, onRetry }) {
  return (
    <div style={{ padding: 40, textAlign: "center", color: "var(--red-600)" }}>
      <p style={{ margin: "0 0 12px" }}>{message}</p>
      <button onClick={onRetry} style={{ padding: "6px 14px", border: "1px solid var(--c-200)", background: "var(--surface)", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>Try Again</button>
    </div>
  );
}

const TH = { padding: "10px 12px", fontSize: 12, fontWeight: 600, color: "var(--c-600)", borderBottom: "1px Solid var(--c-200)", whiteSpace: "nowrap" };
const TD = { padding: "10px 12px", whiteSpace: "nowrap" };

function PositionBadge({ type }) {
  const t = String(type || "").toLowerCase();
  let bg = "var(--red-50)", color = "var(--red-600)", label = type || "Not Assigned";
  if (t.includes("full") || t === "ft") { bg = "var(--blue-100)"; color = "var(--blue-700)"; label = "FT"; }
  else if (t === "fixed" || t === "fixedpay" || t.includes("performance")) { bg = "var(--green-100)"; color = "var(--green-700)"; label = "Fixed"; }
  return <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, background: bg, color, fontWeight: 500 }}>{label}</span>;
}

function BatchCard({ batch, onApprove, onRevert, showApprove }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ border: "1px solid var(--c-200)", borderRadius: 10, background: "var(--surface)" }}>
      <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 14, cursor: "pointer" }} onClick={() => setExpanded(!expanded)}>
        <div style={{ flex: 1 }}>
          <strong style={{ fontSize: 14 }}>Batch {batch.batch_number}</strong>
          <span style={{ marginLeft: 10, fontSize: 12, color: "var(--c-500)" }}>{batch.employee_count} Employees · ₹{Math.round(batch.total_net_pay).toLocaleString()}</span>
          {batch.created_at && <span style={{ marginLeft: 10, fontSize: 11, color: "var(--c-400)" }}>{new Date(batch.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {batch.stage === "stage_3" && <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, background: "var(--green-100)", color: "var(--green-700)", fontWeight: 600 }}>✓ Approved</span>}
          {batch.payout_file_url && <a href={`/api/FinanceAndPayroll/batch-file/${batch.id}/`} onClick={e => e.stopPropagation()} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 12px", border: "1px Solid var(--c-200)", borderRadius: 6, fontSize: 12, color: "var(--blue-700)", textDecoration: "none", background: "var(--surface)" }}><Download size={12} /> Sheet</a>}
          {showApprove && <button onClick={e => { e.stopPropagation(); onApprove(batch.id); }} style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 14px", background: "var(--green-600)", color: "var(--surface)", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" }}><Check size={13} /> Approve</button>}
          {showApprove && onRevert && <button onClick={e => { e.stopPropagation(); onRevert(batch.id); }} style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 14px", background: "var(--red-600)", color: "var(--surface)", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>↩ Revert</button>}
          <ChevronDown size={16} style={{ color: "var(--c-400)", transform: expanded ? "rotate(180deg)" : "none", transition: "0.15s" }} />
        </div>
      </div>
      {reapprovalNote(batch) && <div style={{ padding: "8px 20px", fontSize: 12, color: "var(--amber-600)", background: "var(--amber-50)", borderTop: "1px solid var(--c-100)" }}>{reapprovalNote(batch)}</div>}
      {expanded && (
        <div style={{ borderTop: "1px solid var(--c-100)", padding: "10px 20px", overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead><tr style={{ borderBottom: "1px solid var(--c-200)" }}><th style={{ ...TH, textAlign: "left" }}>Name</th><th style={{ ...TH, textAlign: "left" }}>Department</th><th style={{ ...TH, textAlign: "left" }}>Account</th><th style={{ ...TH, textAlign: "right" }}>Net Pay</th><th style={{ ...TH, textAlign: "center" }}>Status</th></tr></thead>
            <tbody>
              {(batch.employees || []).map(emp => (
                <tr key={emp.id} style={{ borderBottom: "1px solid var(--surface-soft)" }}>
                  <td style={{ padding: "7px 12px" }}>{emp.name}</td>
                  <td style={{ padding: "7px 12px", color: "var(--c-500)" }}>{emp.department}</td>
                  <td style={{ padding: "7px 12px", whiteSpace: "nowrap" }}>{accountLabel(emp)}{emp.bank_changed && <span style={{ marginLeft: 6, fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "var(--amber-50)", color: "var(--amber-600)" }}>Changed</span>}</td>
                  <td style={{ padding: "7px 12px", textAlign: "right", fontWeight: 600, color: "var(--green-600)" }}>₹{Math.round(emp.net_pay).toLocaleString()}</td>
                  <td style={{ padding: "7px 12px", textAlign: "center" }}><span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: emp.finance_status === "Approved" ? "var(--green-100)" : "var(--amber-50)", color: emp.finance_status === "Approved" ? "var(--green-700)" : "var(--amber-600)" }}>{emp.finance_status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
